import { StaffAccountForm } from './staff-account-form';
import { useEffect, useState } from 'react';
import type { ActiveSession } from '../../lib/auth';
import { pinRequest, validStaffPin, type PinMember } from '../../lib/staff-pin';
export function StaffPinManagement({ session }: { session: ActiveSession }) {
  const orgs = session.organizationContexts.filter(
    (c) => c.role === 'admin' && c.actions.includes('manage_staff'),
  );
  const [org, setOrg] = useState(orgs[0]?.organizationId ?? ''),
    [members, setMembers] = useState<PinMember[]>([]),
    [user, setUser] = useState(''),
    [label, setLabel] = useState(''),
    [pin, setPin] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    if (org)
      void pinRequest('/staff-pin/' + org, undefined, true).then(
        (x) => {
          if (live) setMembers(x as PinMember[]);
        },
        () => {
          if (live) setNotice('โหลดรายชื่อสมาชิกไม่ได้');
        },
      );
    return () => {
      live = false;
    };
  }, [org]);
  if (!orgs.length || session.offline) return null;
  const save = async (disable: boolean) => {
    setBusy(true);
    setNotice('');
    try {
      await pinRequest(
        '/staff-pin',
        { organizationId: org, userId: user, disable, ...(!disable ? { label, pin } : {}) },
        true,
      );
      setNotice(
        disable ? 'ปิด PIN แล้ว' : 'บันทึก PIN แล้ว · แจ้ง PIN ให้เจ้าของบัญชีผ่านช่องทางส่วนตัว',
      );
      setPin('');
      setMembers((await pinRequest('/staff-pin/' + org, undefined, true)) as PinMember[]);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'บันทึกไม่ได้');
    } finally {
      setBusy(false);
    }
  };
  return (
    <details className="card staff-pin-management">
      <summary>จัดการบัญชีและ PIN ทีมงาน</summary>
      <StaffAccountForm session={session} organizationId={org} />
      <p>
        กำหนดหรือเปลี่ยน PIN โดยไม่เปลี่ยนสิทธิ์ ชื่อที่แสดงจะปรากฏในหน้าเข้าสู่ระบบ
        และต้องไม่ซ้ำกัน
      </p>
      <label>
        องค์กร
        <select
          value={org}
          onChange={(e) => {
            setOrg(e.target.value);
            setUser('');
          }}
        >
          {orgs.map((c) => (
            <option key={c.organizationId} value={c.organizationId}>
              {c.label ?? c.organizationId}
            </option>
          ))}
        </select>
      </label>
      <label>
        สมาชิกทีม
        <select
          value={user}
          onChange={(e) => {
            setUser(e.target.value);
            setLabel(members.find((m) => m.userId === e.target.value)?.label ?? '');
            setPin('');
          }}
        >
          <option value="">เลือกสมาชิก</option>
          {members.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name} · {m.enabled ? 'เปิด PIN' : 'ยังไม่เปิด PIN'}
            </option>
          ))}
        </select>
      </label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save(false);
        }}
      >
        <label>
          ชื่อสาธารณะสำหรับเข้าสู่ระบบ
          <input
            required
            maxLength={100}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <label>
          PIN ใหม่ 6 หลัก
          <input
            required
            type="password"
            inputMode="numeric"
            pattern="[0-9]{6}"
            minLength={6}
            maxLength={6}
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
        </label>
        <div className="pin-actions">
          <button
            className="action"
            disabled={busy || !user || !label.trim() || !validStaffPin(pin) || !navigator.onLine}
          >
            กำหนด / เปลี่ยน PIN
          </button>
          <button
            type="button"
            disabled={
              busy || !user || !members.find((m) => m.userId === user)?.enabled || !navigator.onLine
            }
            onClick={() => void save(true)}
          >
            ปิด PIN
          </button>
        </div>
      </form>
      {notice ? <p role="status">{notice}</p> : null}
    </details>
  );
}
